// File: typescript/apps/front-end/src/main/discussion/ThoughtViewTracker.ts
import React from "react";
import { PageViewTracker } from "@/main/common/PageViewTracker";
import { ClientFactory } from "@/main/common/ClientFactory";

/**
 * The thought view beacon (round 7 slice 2c, D5/A4): `POST /ajax/thought/count_view/{thoughtPublicId}` once per
 * focused-anchor load of `/discussion/{slug}/thought/{id}`, the discussion beacon's shape.
 *
 * <p>A view is a FOCUSED load, never an impression (design §4): the discussion page is on its `opening` section
 * with an anchor id only on that route, so a thought card rendered in any list never reaches this.
 *
 * <p>Through the generated client (rule 6): the endpoint is `permitAll` but deliberately NOT CSRF-exempt, and the
 * shared axios instance is what carries the XSRF header. The server dedupes per (thought, viewer) for 15 minutes.
 *
 * Design: designs/01-now/2026-07-30-thought-as-first-class-contribution-design.md §4 "the beacon".
 */
export function createThoughtViewTracker(thoughtPublicId: string): PageViewTracker {
    return new PageViewTracker({
        errorMessage: "Failed to record thought view",
        context: { thoughtPublicId },
        // `async` so a synchronous throw from the client becomes a rejection the tracker already handles.
        trackView: async () => ClientFactory.getAjaxThoughtRestControllerApi().countThoughtView(thoughtPublicId),
    });
}

export function useFocusedThoughtViewBeacon({
    section,
    thoughtPublicId,
    isDeleted = false,
}: {
    section?: string | null;
    thoughtPublicId?: string | null;
    isDeleted?: boolean;
}): void {
    const anchor = section === "opening" && !isDeleted ? (thoughtPublicId?.trim() || null) : null;

    // Keyed on the anchor alone: a re-render of the same page instance keeps its tracker (one view), a soft hop to
    // another thought is a new focused load, and leaving the focused section stops a beacon that has not fired.
    React.useEffect(() => {
        if (!anchor) {
            return;
        }
        const tracker = createThoughtViewTracker(anchor);
        tracker.startViewTracking();
        return () => tracker.stopViewTracking();
    }, [anchor]);
}
